import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
type Lang = "en" | "zh";

const LANG_STORAGE_KEY = "trevia.lang";

function getInitialLang(): Lang {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "en" || stored === "zh") return stored;
  } catch {
    // localStorage may throw in private browsing or sandboxed iframes
  }
  if (window.navigator?.language?.toLowerCase().startsWith("zh")) return "zh";
  return "en";
}

const strings = {
  en: {
    /* ── Nav ── */
    nav: { home: "Home", about: "About", freight: "Trevia Freight", sourcing: "Trevia Sourcing", catalog: "Trevia Catalog", blog: "Blog", ship: "ShipTogether", contact: "Contact" },

    /* ── Home ── */
    heroTitle: "Powering Global Trade — From Sourcing & Freight to Smart Shipping.",
    heroSub: "Trevia Group unites logistics, sourcing, and a decentralized container-sharing platform. Built for Chinese exporters and European SMEs.",
    ctas: { exporters: "For Chinese Exporters → Trevia Freight", smes: "For European Businesses → Trevia Sourcing", ship: "Explore ShipTogether Platform" },
    whoWeServe: "Who We Serve",
    chineseExporters: "Chinese Exporters (CN → EU)",
    europeanSMEs: "European SMEs (Import from China)",
    ourDivisions: "Our Divisions",
    whyTrevia: "Why Trevia Group",
    values: ["Innovation — tech-enabled, data-driven", "Transparency — pricing & process clarity", "Sustainability — smarter routes & consolidation"],
    innovationHighlight: "Innovation Highlight",
    innovationBody: "ShipTogether is Trevia Group's decentralized container-sharing platform — enabling shippers and carriers to optimize capacity globally.",
    globalPresence: "Global Presence",
    visitShipTogether: "Visit ShipTogether.com",
    ctaReady: "Ready to simplify your supply chain?",

    /* ── About ── */
    aboutTitle: "About Trevia Group",
    aboutSub: "Trevia Group connects global trade through logistics, sourcing, and smart shipping solutions. We build long-term partnerships with companies that value efficiency, reliability, and transparent cooperation.",
    aboutMissionTitle: "Our Mission",
    aboutMission: "Our mission is to simplify international trade between Asia and Europe by combining technology, logistics expertise, and sourcing intelligence. We focus on building repeat partnerships that grow stronger over time — because long-term trust always delivers better results than one-time transactions.",
    aboutVisionTitle: "Our Vision",
    aboutVision: "We believe in a connected trade ecosystem where businesses of every size can move products across borders with confidence. From verified suppliers to last-mile delivery, we aim to be a dependable extension of our partners' operations.",
    aboutValuesTitle: "Our Values",
    aboutValues: [
      { title: "Partnership First", desc: "We invest in relationships, not transactions. Every client is treated as a long-term partner." },
      { title: "Responsiveness", desc: "Our partners operate in fast-moving markets — and so do we. We stay available, connected, and proactive." },
      { title: "Reliability", desc: "From supplier verification to shipment tracking, we deliver the consistency that global trade depends on." },
      { title: "Transparency", desc: "Clear communication, traceable logistics, and open pricing — our way of building lasting trust." },
    ],
    aboutStoryTitle: "Our Story",
    aboutStory: [
      "Trevia Group was established on a foundation of strong, multi-year partnerships with suppliers, clients, and logistics providers across Europe and Asia.",
      "From day one, our goal has been to bring fresh thinking and modern efficiency to international trade — uniting experience, technology, collaboration, and systematic optimisation.",
      "We are developing and implementing smart, scalable solutions that deliver measurable and meaningful results while creating long-term value for our partners.",
      "Every relationship we build reinforces a network focused on symbiotic growth, shared success, and lasting trust in global logistics.",
    ],
    aboutCTA: "Let's Build Trade Partnerships That Last",
    aboutCTASub: "Whether you're shipping, sourcing, or scaling — Trevia Group is ready to connect your supply chain with confidence.",
    aboutCTABtn: "Contact Trevia Group",

    /* ── Freight ── */
    freightTitle: "Your Reliable China-to-Europe Freight Partner",
    freightSub: "Airport / Seaport / Rail handovers in NL • BE • DE • LU • PL. FTL / LTL / Van to middle & last mile. Transparent pricing, same-day quotes.",
    freightQuote: "Request a Freight Quote",
    freightServicesTitle: "What we do best",
    freightServices: [
      { title: "Airport Collections", desc: "Recovery from major EU airports with slot/appointment options." },
      { title: "Seaport Collections", desc: "FCL/LCL handover and drayage with demurrage minimization." },
      { title: "Rail Terminal Collections", desc: "From key rail hubs across Europe (e.g., Duisburg, Hamburg)." },
      { title: "Middle & Last Mile", desc: "FTL/LTL/Van delivery. Reliable B2B deliveries with POD." },
    ],
    freightWhoTitle: "Who we are",
    freightWho: "We provide reliable handovers and delivery across the Netherlands, Belgium, Germany, Luxembourg and Poland. With a partnership-led model and tight process control, we focus on fast response, transparent pricing and on-time delivery with POD.",
    freightValues: [
      "Speed — same-day quotes, quick appointments",
      "Transparency — clear all-in pricing & updates",
      "Reliability — predictable lead times, POD",
      "Responsiveness — WhatsApp · WeChat · Email",
      "Helpfulness — practical guidance when needed",
    ],
    freightFormTitle: "Quick Quote",
    freightSpot: "Spot (one-off)",
    freightTariff: "Tariff (recurring)",
    freightFieldShipment: "Shipment Details",
    freightFieldYou: "Your Information",
    freightMode: "Mode",
    freightChoose: "Choose",
    freightOrigin: "Origin",
    freightOriginPh: "e.g. Shanghai / FRA",
    freightDest: "Destination",
    freightDestPh: "e.g. Warsaw / Rotterdam",
    freightIncoterms: "Incoterms",
    freightIncotermsPh: "EXW / FOB / DDP / DAP",
    freightWeight: "Weight (kg)",
    freightWeightPh: "e.g. 1200",
    freightVolume: "Volume (m³)",
    freightVolumePh: "e.g. 2.4",
    freightDetails: "Details (dims, HS codes, special handling)",
    freightDetailsPh: "Pallet count, dimensions, HS codes, ADR, appointment, etc.",
    freightCompany: "Company",
    freightContact: "Contact",
    freightEmail: "Email",
    freightPhone: "Phone / WeChat",
    freightSubmit: "Submit Quote Request",
    freightOrEmail: "or email",

    /* ── Sourcing ── */
    sourcingTitle: "Simplify Your Sourcing from China",
    sourcingSub: "Supplier verification • Quality control • Customs & compliance • Logistics integration • Catalog of pre-sourced products.",
    sourcingCTA: "Start Your Sourcing Project",
    sourcingBrowse: "Browse Trevia Catalog",
    sourcingProcessTitle: "Our Process",
    sourcingSteps: [
      { title: "Inquiry", desc: "Tell us what you need — we analyze your requirements and provide a tailored sourcing plan." },
      { title: "Supplier Verification", desc: "We verify factories for reliability, production capacity, certifications, and compliance with EU standards." },
      { title: "Quality Control", desc: "From pre-production to shipment, our inspectors ensure your goods meet quality expectations and specifications." },
      { title: "Customs & Compliance", desc: "We handle export documentation, HS codes, product certificates, and import customs clearance support." },
      { title: "Logistics & Delivery", desc: "Integrated logistics via Trevia Freight — sea, rail, or air — with full tracking and Europe-wide delivery." },
    ],
    sourcingBottomCTA: "Ready to Source Smarter?",
    sourcingBottomSub: "From supplier verification to Europe-wide delivery — let Trevia Group handle your sourcing end-to-end.",
    sourcingBrowseCatalog: "Browse Catalog",

    /* ── Blog ── */
    blogTitle: "Blog",
    blogSub: "Expert insights on China-Europe freight, sourcing, compliance, and logistics.",
    blogSearch: "Search articles...",
    blogReadMore: "Read more",
    blogShareArticle: "Share",
    blogCopyLink: "Copy link",
    blogCopied: "Copied!",
    blogTableOfContents: "Table of Contents",

    /* ── Catalog ── */
    catalogTitle: "Trevia Catalog",
    catalogSub: "Curated portfolio of pre-sourced products from verified suppliers.",
    catalogAll: "All",
    catalogView: "View details",
    catalogMOQ: "MOQ:",
    catalogLead: "Lead:",
    catalogVerified: "✔ Verified Supplier",
    catalogSample: "Order Sample",
    catalogBulk: "Request Bulk Quote",
    catalogDisclaimer: "Pricing is indicative and subject to order quantity, customization, and logistics.",

    /* ── Product Detail ── */
    productBackToCatalog: "Back to Catalog",
    productRequestQuote: "Request Quote",
    productUnits: "units",
    productPrice: "Price",
    productLeadTime: "Lead Time",
    productSamplePrice: "Sample Price",
    productTrackRecord: "Proven Track Record",
    productUnitsDelivered: "units delivered",
    productShippedViaTrevia: "shipped via Trevia",
    productClientRating: "client rating",
    productDeliveredTo: "delivered to",
    productFirstShipped: "First shipped",
    productLastShipped: "Last shipped",
    productCertifications: "Certifications",
    productSpecifications: "Specifications",
    productCustomization: "Customization Options",
    productReorder: "Reorder This Product",
    productAskQuestion: "Ask a Question",
    productShipsViaTrevia: "Ships via Trevia Freight",

    /* ── Contact ── */
    contactTitle: "Get in touch",
    contactSub: "Let's build reliable, transparent trade partnerships together.",
    topic: "Topic", name: "Name", email: "Email", company: "Company", message: "Message", submit: "Submit",
    contactPhone: "Phone (optional)",
    contactPhonePh: "+386 31 708 897",
    contactMsgPh: "Tell us about your shipment or sourcing request…",
    contactTopics: ["General", "Freight", "Sourcing", "Catalog"],
    contactSelect: "Select…",
    contactDirect: "Direct Contact",
    contactEmailLabel: "Email",
    contactPhoneLabel: "Phone / WhatsApp",
    contactWechatLabel: "WeChat",
    contactWechatValue: "Available on request",
    contactOffice: "Office",
    contactResponseTime: "Response Time",

    /* ── ShipTogether ── */
    shipTitle: "ShipTogether — A Trevia Group Platform",
    shipFeaturesTitle: "Key Features",
    shipFeatures: [
      { title: "Transparency", desc: "Real-time capacity visibility and allocations." },
      { title: "Cost Sharing", desc: "Collaborative consolidation to lower unit costs." },
      { title: "Digital Contracts", desc: "Digitally signed commitments and milestones." },
      { title: "Global Community", desc: "Shippers & carriers across major trade lanes." },
    ],

    /* ── Success ── */
    successTitle: "Thank you!",
    successMsg: "We've received your message. Our team will get back to you shortly.",
    successHome: "Back to Home",
    successCatalog: "Browse Catalog",

    /* ── Footer ── */
    footerTagline: "Global Trade. Smart Connections.",
    footerContact: "Contact",
    footerNav: "Navigation",
    footerCompany: "Company",
    footerCopyright: "All rights reserved.",
    replySLA: "We reply within 2 hours (CET) on business days.",
    wechat: "WeChat", businessCard: "Business Card",

    /* ── DivisionCards ── */
    divisions: [
      { title: "Trevia Freight", desc: "China→Europe freight: sea, rail, air, customs, warehousing.", cta: "Learn more" },
      { title: "Trevia Sourcing", desc: "Supplier verification, QC, logistics — built for European SMEs.", cta: "Learn more" },
      { title: "ShipTogether", desc: "Decentralized container sharing to optimize global capacity.", cta: "Visit ShipTogether.com" },
    ],

    /* ── Related Content ── */
    relatedReading: "Related Reading",

    /* ── FAQ ── */
    faqTitle: "Frequently Asked Questions",
    freightFaqs: [
      { q: "How long does shipping from China to Europe take?", a: "Transit times vary by mode: air freight takes 3-7 days, rail 18-25 days, and sea freight 30-45 days. We help you choose the optimal mode based on your urgency and budget." },
      { q: "What's the difference between LCL and FCL?", a: "FCL (Full Container Load) means you book an entire container. LCL (Less-than-Container Load) means your goods share space with other shippers. FCL is more cost-effective per CBM for larger volumes, while LCL is ideal for smaller shipments." },
      { q: "Do you handle customs clearance?", a: "We provide customs guidance including HS code classification, documentation preparation, and connections to customs brokers across NL, BE, DE, LU, and PL. We ensure your shipments clear customs smoothly." },
      { q: "What are your freight rates?", a: "Rates depend on mode, volume, and route. We provide transparent, same-day quotes with no hidden fees. Request a quote through our form and we'll respond within hours." },
    ],
    sourcingFaqs: [
      { q: "How do you verify Chinese suppliers?", a: "We follow a 5-step verification process: business license verification, factory audit (on-site or remote), product certification checks, sample testing, and reference checks from existing European clients." },
      { q: "What's the minimum order quantity?", a: "MOQs vary by product and supplier. Our catalog shows MOQs for each item. For custom sourcing projects, we negotiate competitive MOQs on your behalf — often lower than what suppliers offer directly." },
      { q: "Can you handle quality control inspections?", a: "Yes. We offer pre-production, during-production, and pre-shipment inspections through our on-the-ground team in China. Every sourcing project includes at least one QC checkpoint." },
      { q: "How long does the sourcing process take?", a: "A typical sourcing project takes 4-8 weeks from briefing to first shipment: 1-2 weeks for supplier identification and verification, 2-3 weeks for sampling, and 1-3 weeks for production (depending on MOQ)." },
    ],

    /* ── Filters (kept for compat) ── */
    filters: { all: "All", eco: "Eco Packaging", home: "Home", tech: "Tech", gifts: "Gifts" },
    orderSample: "Order Sample", requestQuote: "Request Bulk Quote", supplierVerified: "Verified Supplier",
  },

  zh: {
    /* ── Nav ── */
    nav: { home: "首页", about: "关于我们", freight: "Trevia 货运", sourcing: "Trevia 采购", catalog: "Trevia 产品目录", blog: "博客", ship: "ShipTogether", contact: "联系" },

    /* ── Home ── */
    heroTitle: "从采购与货运到智能拼箱，驱动全球贸易。",
    heroSub: "Trevia Group 融合物流、采购与去中心化拼箱平台，服务中国出口商与欧洲中小企业。",
    ctas: { exporters: "服务中国出口商 → Trevia 货运", smes: "服务欧洲企业 → Trevia 采购", ship: "了解 ShipTogether 平台" },
    whoWeServe: "服务对象",
    chineseExporters: "中国出口商（中国 → 欧盟）",
    europeanSMEs: "欧洲中小企业（中国进口）",
    ourDivisions: "业务版块",
    whyTrevia: "选择 Trevia Group",
    values: ["创新 — 科技驱动，数据赋能", "透明 — 价格与流程清晰", "可持续 — 更优路线与合并运输"],
    innovationHighlight: "创新亮点",
    innovationBody: "ShipTogether 是 Trevia Group 的去中心化拼箱平台，帮助托运人与承运人优化全球舱位。",
    globalPresence: "全球网络",
    visitShipTogether: "访问 ShipTogether.com",
    ctaReady: "准备好简化您的供应链了吗？",

    /* ── About ── */
    aboutTitle: "关于 Trevia Group",
    aboutSub: "Trevia Group 通过物流、采购和智能运输解决方案连接全球贸易。我们与重视效率、可靠性和透明合作的企业建立长期合作伙伴关系。",
    aboutMissionTitle: "我们的使命",
    aboutMission: "我们的使命是通过结合技术、物流专业知识和采购情报，简化亚洲与欧洲之间的国际贸易。我们专注于建立持续增长的长期合作关系——因为长期信任总是比一次性交易带来更好的结果。",
    aboutVisionTitle: "我们的愿景",
    aboutVision: "我们相信一个互联互通的贸易生态系统，让各种规模的企业都能自信地跨境运输产品。从认证供应商到最后一公里配送，我们致力于成为合作伙伴运营的可靠延伸。",
    aboutValuesTitle: "我们的价值观",
    aboutValues: [
      { title: "合作伙伴至上", desc: "我们投资的是关系，而非交易。每位客户都被视为长期合作伙伴。" },
      { title: "积极响应", desc: "我们的合作伙伴在快速发展的市场中运营——我们也是如此。我们保持可用、互联和主动。" },
      { title: "可靠性", desc: "从供应商验证到货运追踪，我们提供全球贸易所依赖的一致性。" },
      { title: "透明度", desc: "清晰的沟通、可追溯的物流和透明的定价——这是我们建立持久信任的方式。" },
    ],
    aboutStoryTitle: "我们的故事",
    aboutStory: [
      "Trevia Group 建立在与欧洲和亚洲供应商、客户及物流服务商多年深厚合作的基础上。",
      "从第一天起，我们的目标就是为国际贸易带来全新思维和现代效率——融合经验、技术、协作和系统化优化。",
      "我们正在开发和实施智能、可扩展的解决方案，为合作伙伴创造可衡量的、有意义的成果和长期价值。",
      "我们建立的每一段关系都在巩固一个专注于共生增长、共同成功和全球物流持久信任的网络。",
    ],
    aboutCTA: "让我们建立持久的贸易合作关系",
    aboutCTASub: "无论您是运输、采购还是扩展规模——Trevia Group 都将自信地连接您的供应链。",
    aboutCTABtn: "联系 Trevia Group",

    /* ── Freight ── */
    freightTitle: "可靠的中欧跨境货运伙伴",
    freightSub: "荷兰·比利时·德国·卢森堡·波兰的机场/港口/铁路交接。整车/零担/厢式货车至中转及最后一公里。透明定价，当天报价。",
    freightQuote: "获取货运报价",
    freightServicesTitle: "我们的核心服务",
    freightServices: [
      { title: "机场提货", desc: "从欧洲主要机场提货，支持预约时段选项。" },
      { title: "港口提货", desc: "整箱/拼箱交接和短驳运输，最小化滞期费。" },
      { title: "铁路终端提货", desc: "从欧洲主要铁路枢纽提货（如杜伊斯堡、汉堡）。" },
      { title: "中转及最后一公里", desc: "整车/零担/厢式货车配送。可靠的B2B配送，含签收证明。" },
    ],
    freightWhoTitle: "关于我们",
    freightWho: "我们在荷兰、比利时、德国、卢森堡和波兰提供可靠的交接和配送服务。凭借以合作伙伴为导向的模式和严格的流程控制，我们专注于快速响应、透明定价和准时交付。",
    freightValues: [
      "速度 — 当天报价，快速预约",
      "透明 — 全包价格与实时更新",
      "可靠 — 可预测的交付时间与签收证明",
      "响应 — WhatsApp · 微信 · 邮件",
      "服务 — 需要时提供实用指导",
    ],
    freightFormTitle: "快速报价",
    freightSpot: "即时（一次性）",
    freightTariff: "合同价（长期）",
    freightFieldShipment: "货运详情",
    freightFieldYou: "您的信息",
    freightMode: "运输方式",
    freightChoose: "请选择",
    freightOrigin: "起运地",
    freightOriginPh: "如：上海 / FRA",
    freightDest: "目的地",
    freightDestPh: "如：华沙 / 鹿特丹",
    freightIncoterms: "贸易术语",
    freightIncotermsPh: "EXW / FOB / DDP / DAP",
    freightWeight: "重量（公斤）",
    freightWeightPh: "如：1200",
    freightVolume: "体积（立方米）",
    freightVolumePh: "如：2.4",
    freightDetails: "详情（尺寸、HS编码、特殊处理）",
    freightDetailsPh: "托盘数量、尺寸、HS编码、危险品、预约等",
    freightCompany: "公司",
    freightContact: "联系人",
    freightEmail: "邮箱",
    freightPhone: "电话 / 微信",
    freightSubmit: "提交报价请求",
    freightOrEmail: "或发邮件至",

    /* ── Sourcing ── */
    sourcingTitle: "为欧洲企业简化中国采购",
    sourcingSub: "供应商验证 • 质量控制 • 海关合规 • 物流整合 • 预采购产品目录。",
    sourcingCTA: "开启采购项目",
    sourcingBrowse: "浏览 Trevia 产品目录",
    sourcingProcessTitle: "我们的流程",
    sourcingSteps: [
      { title: "需求咨询", desc: "告诉我们您的需求——我们分析您的要求，提供量身定制的采购方案。" },
      { title: "供应商验证", desc: "我们验证工厂的可靠性、生产能力、资质认证和欧盟标准合规性。" },
      { title: "质量控制", desc: "从生产前到发货，我们的质检人员确保您的货物符合质量要求和规格。" },
      { title: "海关与合规", desc: "我们处理出口文件、HS编码、产品证书和进口清关支持。" },
      { title: "物流与配送", desc: "通过 Trevia 货运进行一体化物流——海运、铁路或空运——全程追踪，欧洲全境配送。" },
    ],
    sourcingBottomCTA: "准备好更智能地采购了吗？",
    sourcingBottomSub: "从供应商验证到欧洲全境配送——让 Trevia Group 为您提供端到端采购服务。",
    sourcingBrowseCatalog: "浏览产品目录",

    /* ── Blog ── */
    blogTitle: "博客",
    blogSub: "中国至欧洲货运、采购、合规及物流的专业见解。",
    blogSearch: "搜索文章...",
    blogReadMore: "阅读更多",
    blogShareArticle: "分享",
    blogCopyLink: "复制链接",
    blogCopied: "已复制！",
    blogTableOfContents: "目录",

    /* ── Catalog ── */
    catalogTitle: "Trevia 产品目录",
    catalogSub: "精选预采购产品组合，来自认证供应商。",
    catalogAll: "全部",
    catalogView: "查看详情",
    catalogMOQ: "起订量：",
    catalogLead: "交期：",
    catalogVerified: "✔ 供应商已认证",
    catalogSample: "订购样品",
    catalogBulk: "批量报价",
    catalogDisclaimer: "价格为参考价，受订购量、定制化和物流影响。",

    /* ── Product Detail ── */
    productBackToCatalog: "返回产品目录",
    productRequestQuote: "请求报价",
    productUnits: "件",
    productPrice: "价格",
    productLeadTime: "交付周期",
    productSamplePrice: "样品价格",
    productTrackRecord: "验证的交付记录",
    productUnitsDelivered: "件已交付",
    productShippedViaTrevia: "次通过 Trevia 运输",
    productClientRating: "客户评分",
    productDeliveredTo: "已交付至",
    productFirstShipped: "首次发货",
    productLastShipped: "最近发货",
    productCertifications: "认证",
    productSpecifications: "规格参数",
    productCustomization: "定制选项",
    productReorder: "再次订购此产品",
    productAskQuestion: "咨询问题",
    productShipsViaTrevia: "通过 Trevia 货运发货",

    /* ── Contact ── */
    contactTitle: "联系我们",
    contactSub: "让我们共同建立可靠、透明的贸易合作关系。",
    topic: "主题", name: "姓名", email: "邮箱", company: "公司", message: "留言", submit: "提交",
    contactPhone: "电话（选填）",
    contactPhonePh: "+386 31 708 897",
    contactMsgPh: "请告诉我们您的货运或采购需求…",
    contactTopics: ["综合咨询", "货运", "采购", "产品目录"],
    contactSelect: "请选择…",
    contactDirect: "直接联系",
    contactEmailLabel: "邮箱",
    contactPhoneLabel: "电话 / WhatsApp",
    contactWechatLabel: "微信",
    contactWechatValue: "按需提供",
    contactOffice: "办公地址",
    contactResponseTime: "响应时间",

    /* ── ShipTogether ── */
    shipTitle: "ShipTogether — Trevia Group 平台",
    shipFeaturesTitle: "核心功能",
    shipFeatures: [
      { title: "透明度", desc: "实时舱位可视化和分配。" },
      { title: "费用共享", desc: "协作拼柜，降低单位成本。" },
      { title: "数字合同", desc: "数字签署的承诺和里程碑。" },
      { title: "全球社区", desc: "覆盖主要贸易航线的托运人与承运人。" },
    ],

    /* ── Success ── */
    successTitle: "感谢您！",
    successMsg: "我们已收到您的消息，团队将尽快回复。",
    successHome: "返回首页",
    successCatalog: "浏览产品目录",

    /* ── Footer ── */
    footerTagline: "全球贸易，智慧连接。",
    footerContact: "联系方式",
    footerNav: "导航",
    footerCompany: "公司",
    footerCopyright: "版权所有。",
    replySLA: "工作日（中欧时间）2 小时内回复。",
    wechat: "微信", businessCard: "电子名片",

    /* ── DivisionCards ── */
    divisions: [
      { title: "Trevia 货运", desc: "中国至欧洲货运：海运、铁路、空运、报关、仓储。", cta: "了解更多" },
      { title: "Trevia 采购", desc: "供应商验证、质量控制、物流——专为欧洲中小企业打造。", cta: "了解更多" },
      { title: "ShipTogether", desc: "去中心化拼箱平台，优化全球舱位利用。", cta: "访问 ShipTogether.com" },
    ],

    /* ── Related Content ── */
    relatedReading: "相关阅读",

    /* ── FAQ ── */
    faqTitle: "常见问题",
    freightFaqs: [
      { q: "从中国到欧洲的运输需要多长时间？", a: "运输时间因方式而异：空运3-7天，铁路18-25天，海运30-45天。我们根据您的紧急程度和预算帮您选择最佳方式。" },
      { q: "LCL和FCL有什么区别？", a: "FCL（整箱）是指您预订整个集装箱。LCL（拼箱）是指您的货物与其他托运人共享空间。FCL对于较大货量更划算，LCL适合较小货量。" },
      { q: "你们办理清关吗？", a: "我们提供清关指导，包括HS编码分类、文件准备，以及与NL、BE、DE、LU、PL的报关行对接，确保您的货物顺利清关。" },
      { q: "你们的运费是多少？", a: "费率取决于运输方式、体积和路线。我们提供透明的当日报价，无隐藏费用。通过表单提交报价请求，我们将在数小时内回复。" },
    ],
    sourcingFaqs: [
      { q: "你们如何验证中国供应商？", a: "我们遵循5步验证流程：营业执照验证、工厂审核（现场或远程）、产品认证检查、样品测试，以及现有欧洲客户的参考检查。" },
      { q: "最低订购量是多少？", a: "MOQ因产品和供应商而异。我们的产品目录显示每件商品的MOQ。对于定制采购项目，我们代您协商有竞争力的MOQ。" },
      { q: "你们能处理质量控制检验吗？", a: "可以。我们通过驻中国团队提供生产前、生产中和装运前检验。每个采购项目至少包含一个质量检查节点。" },
      { q: "采购流程需要多长时间？", a: "典型的采购项目从简报到首次发货需要4-8周：供应商识别和验证1-2周，打样2-3周，生产1-3周（取决于MOQ）。" },
    ],

    /* ── Filters (kept for compat) ── */
    filters: { all: "全部", eco: "环保包装", home: "家居", tech: "科技", gifts: "礼品" },
    orderSample: "样品下单", requestQuote: "批量报价", supplierVerified: "供应商已认证",
  }
};

type Strings = typeof strings.en;
const I18nContext = createContext<{ lang: Lang; setLang: (l: Lang) => void; t: Strings; }>({ lang: "en", setLang: () => {}, t: strings.en });

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLang] = useState<Lang>(getInitialLang);
  const t = useMemo(() => (lang === "zh" ? strings.zh : strings.en) as Strings, [lang]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, [lang]);

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}
export function useI18n() { return useContext(I18nContext); }
